import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { getSocket } from "@/lib/socket";

/**
 * Mounted only by `ChatListPage` — keeps the conversation list (unread
 * badges, last-message preview, ordering) live without polling.
 * `message_received` fires for every message on every one of the current
 * user's chats (server emits to each participant's personal `user:<id>`
 * room, not just the chat room — see server/src/socket/index.ts), so a
 * message arriving on a conversation the user hasn't opened still updates
 * this list. Simplest correct approach: invalidate and let TanStack Query
 * refetch, rather than hand-patching unread counts/ordering into the cache
 * from a partial socket payload.
 */
export function useConversationListRealtime(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    function refetch() {
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }

    socket.on("message_received", refetch);
    socket.on("chat_created", refetch);

    return () => {
      socket.off("message_received", refetch);
      socket.off("chat_created", refetch);
    };
  }, [queryClient]);
}
